import React, { useCallback, useEffect, useState } from "react";
import { MessageBar, MessageBarType } from "office-ui-fabric-react";
import {
    TimesheetEditApplyResult,
    TimesheetEditBlockReasons,
    TimesheetEditMappingItem,
    TimesheetBulkEditPrepareResult,
    TimesheetEditValidateResult
} from "../../../../../store/TimesheetApprovalStore";
import { ConfirmationDialog } from "../../../../common/ConfirmationDialog";
import { FormatDate } from "../../../../utils/common";
import ApplyResultDialog from "./ApplyResultDialog";
import BulkEditApprovalRequestsPanel from "./BulkEditApprovalRequestsPanel";
import { buildApplyResultMessages, guardListStyle } from "./common";

interface Props {
    requestIds: string[];
    bulkEditPrepare: (requestIds: string[], callback?: (result: TimesheetBulkEditPrepareResult) => void) => void;
    bulkEditValidate: (
        requestIds: string[],
        targetProjectId: string,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditValidateResult) => void
    ) => void;
    bulkEditApply: (
        requestIds: string[],
        targetProjectId: string,
        mapping: TimesheetEditMappingItem[],
        callback?: (result: TimesheetEditApplyResult) => void,
        onError?: () => void
    ) => void;
    onClose: () => void;
}

const BulkEditApprovalRequests = (props: Props) => {
    const { requestIds, bulkEditPrepare, bulkEditValidate, bulkEditApply, onClose } = props;
    const [result, setResult] = useState<TimesheetBulkEditPrepareResult | null>(null);
    const [applyMessages, setApplyMessages] = useState<string[] | null>(null);

    useEffect(() => {
        bulkEditPrepare(requestIds, setResult);
    }, [requestIds]);

    const onApplied = useCallback(
        (applyResult: TimesheetEditApplyResult) => {
            const messages = buildApplyResultMessages(applyResult);
            if (messages.length > 0) {
                setApplyMessages(messages);
            } else {
                onClose();
            }
        },
        [onClose]
    );

    if (applyMessages) {
        return <ApplyResultDialog messages={applyMessages} onDismiss={onClose} />;
    }

    if (!result) {
        return null;
    }

    if (result.blockReason === TimesheetEditBlockReasons.None) {
        return (
            <BulkEditApprovalRequestsPanel
                requestIds={requestIds}
                sourceProject={result.sourceProject!}
                sourceRows={result.sourceRows ?? []}
                bulkEditValidate={bulkEditValidate}
                bulkEditApply={bulkEditApply}
                onApplied={onApplied}
                onClose={onClose}
            />
        );
    }

    const hasReason = (reason: TimesheetEditBlockReasons) => (result.blockReason & reason) !== 0;

    if (hasReason(TimesheetEditBlockReasons.SourceProjectTimeTrackingDisabled)) {
        return (
            <ConfirmationDialog
                onDismiss={onClose}
                onYes={onClose}
                yesButtonProps={{ text: "OK" }}
                dialogContentProps={{ title: "Time tracking is disabled for the source project" }}>
                <p>
                    Bulk edit cannot move time from "{result.sourceProject?.name}" because the Allow time to be tracked for
                    the project setting is turned off for it. Please turn the setting on for the project, then try again.
                </p>
            </ConfirmationDialog>
        );
    }

    if (hasReason(TimesheetEditBlockReasons.MultipleProjects)) {
        return (
            <ConfirmationDialog
                onDismiss={onClose}
                onYes={onClose}
                yesButtonProps={{ text: "OK" }}
                dialogContentProps={{ title: "Multiple projects selected" }}>
                <p>
                    Bulk edit requires all selected approval requests to be from the same project. Your current selection
                    includes:
                </p>
                <ul style={guardListStyle}>
                    {(result.multipleProjectNames ?? []).map(name => (
                        <li key={name}>{name}</li>
                    ))}
                </ul>
                <p>Please update your selection to include approval requests from one project only, then try again.</p>
                <MessageBar messageBarType={MessageBarType.warning}>
                    Bulk Edit applies to project requests only. Administrative time requests are not supported.
                </MessageBar>
            </ConfirmationDialog>
        );
    }

    if (hasReason(TimesheetEditBlockReasons.AdministrativeTime)) {
        return (
            <ConfirmationDialog
                onDismiss={onClose}
                onYes={onClose}
                yesButtonProps={{ text: "OK" }}
                dialogContentProps={{ title: "Administrative Time requests selected" }}>
                <p>Bulk Edit is not available for the selected approval requests.</p>
                <p>
                    Only project approval requests can be bulk edited. Administrative Time approval requests are not
                    supported.
                </p>
            </ConfirmationDialog>
        );
    }

    if (hasReason(TimesheetEditBlockReasons.ClosedPeriods)) {
        return (
            <ConfirmationDialog
                onDismiss={onClose}
                onYes={onClose}
                yesButtonProps={{ text: "OK" }}
                dialogContentProps={{ title: "Requests belonging to closed periods selected" }}>
                <p>
                    Bulk edit can be performed only for approval requests that belong to open time reporting periods. The
                    following periods from your selection are closed:
                </p>
                <ul style={guardListStyle}>
                    {(result.closedPeriods ?? []).map(period => (
                        <li key={`${period.startDate}-${period.endDate}`}>
                            {FormatDate(period.startDate)} - {FormatDate(period.endDate)}
                        </li>
                    ))}
                </ul>
                <p>Please update your selection to include approval requests for open periods only, then try again.</p>
            </ConfirmationDialog>
        );
    }

    return null;
};

export default BulkEditApprovalRequests;
